import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import { useI18n } from "@/context/I18nContext";

// All photos used across the site, categorized
const PHOTOS = [
  // Workforce — uploaded
  { url: "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/fdnhvj4f_ChatGPT%20Image%20May%206%2C%202026%2C%2006_28_49%20AM.png", category: "workforce", caption: { en: "MNH Operations Team", ar: "فريق عمليات الحمادي" }, tag: { en: "Jeddah HQ", ar: "المقر الرئيسي" } },
  { url: "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/tcs7lvj6_ChatGPT%20Image%20May%206%2C%202026%2C%2001_35_56%20AM.png", category: "workforce", caption: { en: "Mohammed Ali", ar: "محمد علي" }, tag: { en: "CEO Portrait", ar: "صورة الرئيس التنفيذي" } },

  // Life at MNH (culture) — uploaded
  { url: "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/eveylfpr_Employee-Engagement-Activities.webp", category: "culture", caption: { en: "Engagement & Wellbeing", ar: "التفاعل والرعاية" }, tag: { en: "Culture", ar: "ثقافة" } },
  { url: "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/x3yy8yqx_360_F_400155626_Gga4YgM9PpyBBNDu6Ce7zPNOWGK2C3OI.jpg", category: "culture", caption: { en: "One Team, One Mission", ar: "فريق واحد، رسالة واحدة" }, tag: { en: "Collaboration", ar: "تعاون" } },
  { url: "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/yihj1u7o_collaborative-process-indian-business-mans-brainstorming-meeting-officediverse-team-young-people-dressed-suits-cooperating-developing-common-project_627829-13765.avif", category: "culture", caption: { en: "Daily Coordination", ar: "التنسيق اليومي" }, tag: { en: "Operations", ar: "عمليات" } },
  { url: "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/9lz52t7k_images.jpg", category: "culture", caption: { en: "Vision 2030 Aligned", ar: "التوافق مع رؤية ٢٠٣٠" }, tag: { en: "Saudi Future", ar: "مستقبل المملكة" } },
  { url: "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/teo0awik_IMG_0633_Edited-1-scaled.jpg", category: "culture", caption: { en: "Off-site Team Building", ar: "فعاليات بناء الفريق" }, tag: { en: "Engagement", ar: "تفاعل" } },

  // Services
  { url: "https://images.unsplash.com/photo-1759745125627-333e78bc1edc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "services", caption: { en: "General Contracting", ar: "المقاولات العامة" }, tag: { en: "Civil & MEP", ar: "مدني وكهروميكانيكي" } },
  { url: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "services", caption: { en: "Construction", ar: "البناء والإنشاءات" }, tag: { en: "Towers & Villas", ar: "أبراج وفلل" } },
  { url: "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/sl5xdsm6_Electro_Mechanical_Assembly_Manufacturing.jpg", category: "em", caption: { en: "Electro Mechanical", ar: "الأعمال الكهروميكانيكية" }, tag: { en: "Control Boards", ar: "لوحات التحكم" } },
  { url: "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "services", caption: { en: "Landscaping", ar: "تنسيق الحدائق" }, tag: { en: "Hardscape & Planting", ar: "أعمال صلبة وتشجير" } },
  { url: "https://images.unsplash.com/photo-1774750264848-d47a68d82122?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "services", caption: { en: "Cleaning Services", ar: "خدمات التنظيف" }, tag: { en: "Industrial Clean", ar: "تنظيف صناعي" } },
  { url: "https://images.unsplash.com/photo-1646662521253-5b9253b1a207?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "services", caption: { en: "Maintenance", ar: "الصيانة" }, tag: { en: "FM & MEP", ar: "إدارة مرافق" } },
  { url: "https://images.unsplash.com/photo-1765574782103-6d326d2240f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "services", caption: { en: "Airport Ground Services", ar: "خدمات المطارات الأرضية" }, tag: { en: "Ramp & GSE", ar: "ممرات ومعدات" } },

  // Electro Mechanical Showcase
  { url: "https://images.unsplash.com/photo-1759745125627-333e78bc1edc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "em", caption: { en: "HVAC Plant Rooms", ar: "غرف التكييف المركزية" }, tag: { en: "Cooling", ar: "تبريد" } },
  { url: "https://images.unsplash.com/photo-1646662521253-5b9253b1a207?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "em", caption: { en: "Power Distribution", ar: "توزيع الكهرباء" }, tag: { en: "MV/LV", ar: "جهد متوسط/منخفض" } },
  { url: "https://images.unsplash.com/photo-1758798219572-512a03a60ce0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "em", caption: { en: "Fire Fighting Systems", ar: "أنظمة إطفاء الحريق" }, tag: { en: "Sprinkler & Alarm", ar: "رشاشات وإنذار" } },
  { url: "https://images.unsplash.com/photo-1774535140090-8f860e44dd0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "em", caption: { en: "Cable Management", ar: "إدارة الكابلات" }, tag: { en: "Tray & Containment", ar: "حوامل وقنوات" } },
  { url: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "em", caption: { en: "Lighting & Power", ar: "الإضاءة والقدرة" }, tag: { en: "Final Connections", ar: "التوصيلات النهائية" } },

  // Projects (skylines / sites)
  { url: "https://images.unsplash.com/photo-1760450117431-5c70c2cc62aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "projects", caption: { en: "Riyadh Tower Phase 2", ar: "برج الرياض المرحلة الثانية" }, tag: { en: "Contracting", ar: "مقاولات" } },
  { url: "https://images.unsplash.com/photo-1758798219572-512a03a60ce0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "projects", caption: { en: "PIF HQ Fit-out", ar: "تشطيبات مقر صندوق الاستثمارات" }, tag: { en: "Riyadh", ar: "الرياض" } },
  { url: "https://images.unsplash.com/photo-1774535140090-8f860e44dd0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "projects", caption: { en: "KAIA Ground Operations", ar: "العمليات الأرضية في مطار الملك عبدالعزيز" }, tag: { en: "Jeddah", ar: "جدة" } },
  { url: "https://images.unsplash.com/photo-1773717872820-1d7eba2acab9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600", category: "projects", caption: { en: "Aramco Site C", ar: "موقع أرامكو C" }, tag: { en: "Eastern Province", ar: "المنطقة الشرقية" } },
];

const SPAN_PATTERNS = [
  "md:col-span-6 lg:col-span-4 row-span-2",
  "md:col-span-6 lg:col-span-4",
  "md:col-span-6 lg:col-span-4",
  "md:col-span-6 lg:col-span-4",
  "md:col-span-6 lg:col-span-4",
  "md:col-span-6 lg:col-span-4 row-span-2",
];

export default function GalleryPage() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";
  const g = t.gallery;
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const filtered = useMemo(() => {
    return filter === "all" ? PHOTOS : PHOTOS.filter((p) => p.category === filter);
  }, [filter]);

  const FILTERS = [
    { key: "all", label: g.filters.all },
    { key: "workforce", label: g.filters.workforce },
    { key: "culture", label: g.filters.culture },
    { key: "services", label: g.filters.services },
    { key: "em", label: g.filters.em },
    { key: "projects", label: g.filters.projects },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="gallery-page">
      <TopBar />
      <Navbar />

      {/* Header */}
      <section className="bg-secondary text-secondary-foreground grain pt-32 pb-20">
        <div className="container-page">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-secondary-foreground/60 hover:text-primary transition-colors"
            data-testid="gallery-back-home"
          >
            <ArrowLeft className={`h-3.5 w-3.5 ${isRtl ? "rotate-180" : ""}`} />
            {g.backHome}
          </Link>
          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="overline text-primary">{g.eyebrow}</span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.05]">
                {g.heading1} <br />
                <span className="text-primary">{g.heading1Accent}</span>
              </h1>
            </div>
            <p className="lg:col-span-4 text-secondary-foreground/65 leading-relaxed">
              {g.description}
            </p>
          </div>
        </div>
      </section>

      {/* Filter chips */}
      <div className="sticky top-[116px] z-30 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="container-page py-5 flex items-center justify-between flex-wrap gap-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="gallery-filters">
            {FILTERS.map((f) => {
              const isActive = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`text-xs uppercase tracking-[0.18em] px-4 py-2 border transition-colors ${
                    isActive
                      ? "bg-secondary text-secondary-foreground border-secondary"
                      : "bg-transparent text-foreground/70 border-border hover:border-primary hover:text-primary"
                  }`}
                  data-testid={`gallery-filter-${f.key}`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="text-xs text-muted-foreground tabular-nums" data-testid="gallery-count">
            {filtered.length} {g.photoCount}
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="container-page py-16">
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[280px]"
          data-testid="gallery-grid"
        >
          <AnimatePresence>
            {filtered.map((p, i) => (
              <motion.figure
                layout
                key={`${p.url}-${i}`}
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.45, delay: (i % 9) * 0.04 }}
                onClick={() => setLightbox(p)}
                className={`relative group overflow-hidden border border-border cursor-pointer ${SPAN_PATTERNS[i % SPAN_PATTERNS.length]}`}
                data-testid={`gallery-photo-${i}`}
              >
                <img
                  src={p.url}
                  alt={p.caption.en}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <figcaption className={`absolute bottom-0 left-0 right-0 p-5 sm:p-6 ${isRtl ? "text-right" : "text-left"}`}>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-primary font-semibold">
                    {p.tag[dir === "rtl" ? "ar" : "en"]}
                  </div>
                  <div className="mt-1.5 text-white text-base sm:text-lg font-bold leading-snug">
                    {p.caption[dir === "rtl" ? "ar" : "en"]}
                  </div>
                  <div className="mt-3 h-px w-10 bg-primary group-hover:w-24 transition-all duration-500" />
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 h-10 w-10 border border-white/30 text-white flex items-center justify-center hover:bg-white hover:text-secondary transition-colors"
              data-testid="gallery-lightbox-close"
              aria-label="Close lightbox"
            >
              <X className="h-4 w-4" />
            </button>
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94 }}
              transition={{ duration: 0.35 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.url} alt={lightbox.caption.en} className="w-full max-h-[78vh] object-contain bg-secondary" />
              <div className="mt-4 text-center text-white">
                <div className="text-[10px] tracking-[0.3em] uppercase text-primary font-semibold">
                  {lightbox.tag[dir === "rtl" ? "ar" : "en"]}
                </div>
                <div className="mt-2 text-xl font-bold">
                  {lightbox.caption[dir === "rtl" ? "ar" : "en"]}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
